// Pure functions for commission calculation logic.
// Used by db.engine.calculateCommission() and tested in isolation.

/**
 * Map a case's product to a category key used by productConfigs.
 * mockData uses product types like "personal_financing" / "credit_card" / "housing_loan"
 * productConfigs use "personal_loan" / "credit_card" / "housing_loan" / "property" / "business_loan" / "government_loan" / "micro_loan"
 */
export function mapProductToCategory(caseRecord) {
  if (!caseRecord) return null;
  const name = (caseRecord.productName || '').toLowerCase();
  const id = (caseRecord.productId || '').toLowerCase();

  if (name.includes('personal') || id.includes('pl-')) return 'personal_loan';
  if (name.includes('credit card') || id.includes('cc-')) return 'credit_card';
  if (name.includes('housing') || name.includes('home financing') || id.includes('hl-')) return 'housing_loan';
  if (name.includes('property')) return 'property';
  if (name.includes('business') || name.includes('sme')) return 'business_loan';
  if (name.includes('government') || name.includes('civil servant') || name.includes('osk') || name.includes('bsn')) return 'government_loan';
  if (name.includes('micro')) return 'micro_loan';
  if (name.includes('auto')) return 'personal_loan';
  return null;
}

/**
 * Find an active commission entry inside a productConfig that matches the recipient.
 * Tries (in order):
 *   1. Exact match by recipientId / specificAgentId
 *   2. Match by agentType (e.g., 'partner', 'individual_agent', 'free_user')
 *   3. First active commission as fallback
 */
export function findActiveCommissionForRecipient(productConfig, recipientUser) {
  if (!productConfig?.commissions || productConfig.commissions.length === 0) return null;
  const active = productConfig.commissions.filter(
    (c) => c.approvalStatus === 'active' && (!c.effectiveTo || new Date(c.effectiveTo) > new Date())
  );
  if (active.length === 0) return null;

  if (recipientUser) {
    // 1. Exact recipient match
    const exact = active.find(
      (c) => c.recipientId === recipientUser.id || c.specificAgentId === recipientUser.id
    );
    if (exact) return exact;
    // 2. Match by agent type / role
    const byRole = active.find(
      (c) => c.agentType === recipientUser.role || c.recipientRole === recipientUser.role
    );
    if (byRole) return byRole;
  }
  // 3. Fallback — first active commission
  return active[0];
}

/**
 * Compute commission amount for a given case + commission entry.
 * - per_case → returns perCaseAmount (fixed RM)
 * - percentage → walks tiers, finds the bracket containing case.amount, applies rate
 *
 * If a case amount is 0 (e.g., credit card), returns the per_case amount even for percentage commissions.
 */
export function computeCommissionAmount(caseRecord, commission) {
  if (!commission) return 0;
  const amount = Number(caseRecord?.amount) || 0;

  if (commission.commissionType === 'per_case') {
    return Number(commission.perCaseAmount) || 0;
  }

  if (commission.commissionType === 'percentage') {
    if (!commission.tiers || commission.tiers.length === 0) return 0;
    if (amount === 0) return 0;

    // Find tier where min <= amount <= max (max=null means unlimited)
    const tier = commission.tiers.find((t) => {
      const min = Number(t.min) || 0;
      const max = t.max === null || t.max === undefined ? Infinity : Number(t.max);
      return amount >= min && amount <= max;
    });
    if (!tier) return 0;
    const rate = Number(tier.rate) || 0;
    return Math.round(amount * rate / 100 * 100) / 100; // round to 2 decimal places
  }

  return 0;
}

/**
 * Top-level helper used by the engine — given a case + db.productConfigs.getAll() + recipient user,
 * returns the commission amount and the commission entry that was used.
 */
export function calculateCommissionForCase(caseRecord, productConfigs, recipientUser) {
  if (!caseRecord || !productConfigs || productConfigs.length === 0) {
    return { amount: 0, commission: null, productConfig: null };
  }
  const category = mapProductToCategory(caseRecord);
  if (!category) return { amount: 0, commission: null, productConfig: null };

  // Prefer exact lender + category match, otherwise any active config in that category
  const matching = productConfigs.filter(
    (pc) => pc.category === category && pc.approvalStatus === 'active'
  );
  let productConfig =
    matching.find((pc) => caseRecord.bank && pc.lenderName?.toLowerCase().includes(caseRecord.bank.toLowerCase().split(' ')[0])) ||
    matching[0];

  if (!productConfig) return { amount: 0, commission: null, productConfig: null };

  const commission = findActiveCommissionForRecipient(productConfig, recipientUser);
  const amount = computeCommissionAmount(caseRecord, commission);

  return { amount, commission, productConfig };
}
